/**
 * Renders semi-transparent AI ghost nodes and their dashed edge lines during an
 * active AI slice-preview session.  When pending nodes exist the layer hides
 * itself because real pending ghost nodes take over the display.
 */
import React from 'react';
import { AiSliceSet, NodeType, OstNode } from '../../types';
import { NODE_W, NODE_H, NODE_GAP_X, LAYER_GAP_Y, COLORS, splitTitle } from '../../utils/canvasUtils';

interface GhostNodeLayerProps {
    aiPreviewSets: AiSliceSet[] | null;
    activeSetIndex: number;
    aiTargetNode: OstNode | null;
    aiNodeType: NodeType | null;
    /** Hide ghost layer when real pending nodes are being placed. */
    hasPendingNodes: boolean;
    nodeMap: Record<string, OstNode & { x: number; y: number }>;
    nodeHeights: Record<string, number>;
}

export default function GhostNodeLayer({
    aiPreviewSets,
    activeSetIndex,
    aiTargetNode,
    aiNodeType,
    hasPendingNodes,
    nodeMap,
    nodeHeights,
}: GhostNodeLayerProps) {
    if (hasPendingNodes) return null;
    if (!aiPreviewSets || !aiTargetNode || !aiNodeType) return null;

    const currentSet = aiPreviewSets[activeSetIndex];
    if (!currentSet) return null;

    const parentPos = nodeMap[aiTargetNode.id];
    if (!parentPos) return null;

    const sliceCount = currentSet.slices.length;
    const totalW = sliceCount * NODE_W + (sliceCount - 1) * (NODE_GAP_X - NODE_W);
    const startX = parentPos.x - totalW / 2 + NODE_W / 2;
    const parentBottom = parentPos.y + (nodeHeights[aiTargetNode.id] || NODE_H) / 2;
    const ghostY = parentPos.y + LAYER_GAP_Y;
    const nodeColor = COLORS[aiNodeType];

    return (
        <>
            {/* Dashed guide edges from parent to ghost positions */}
            {currentSet.slices.map((_, idx) => {
                const ghostX = startX + idx * NODE_GAP_X;
                return (
                    <line
                        key={`ai-edge-${idx}`}
                        x1={parentPos.x}
                        y1={parentBottom}
                        x2={ghostX}
                        y2={ghostY - NODE_H / 2}
                        stroke='#aaa'
                        strokeWidth={1.5}
                        strokeDasharray='4 3'
                        opacity={0.5}
                    />
                );
            })}

            {/* Ghost node rectangles */}
            {currentSet.slices.map((slice, idx) => {
                const ghostX = startX + idx * NODE_GAP_X;
                const titleLines = splitTitle(slice.title);
                return (
                    <g
                        key={`ai-ghost-${idx}`}
                        transform={`translate(${ghostX - NODE_W / 2}, ${ghostY - NODE_H / 2})`}
                        opacity={0.72}
                        pointerEvents='none'
                    >
                        <rect
                            width={NODE_W}
                            height={NODE_H}
                            rx={2}
                            fill={nodeColor?.bg || '#ffffff22'}
                            stroke={nodeColor?.border || '#aaa'}
                            strokeWidth={2}
                            strokeDasharray='5 4'
                        />
                        {/* Type label */}
                        <text
                            x={NODE_W / 2}
                            y={14}
                            fill={nodeColor?.text || '#888'}
                            fontSize={8}
                            textAnchor='middle'
                            fontWeight={700}
                            letterSpacing='0.08em'
                            style={{ fontFamily: 'var(--font-monospace)' }}
                        >
                            {nodeColor?.label || aiNodeType.toUpperCase()}
                        </text>
                        {/* Title — multi-line via splitTitle */}
                        {titleLines.map((line, li) => (
                            <text
                                key={li}
                                x={NODE_W / 2}
                                y={32 + li * 16}
                                fill='#2c2c2c'
                                fontSize={11}
                                textAnchor='middle'
                                style={{ fontFamily: 'var(--font-monospace)' }}
                            >
                                {line}
                            </text>
                        ))}
                    </g>
                );
            })}
        </>
    );
}
